import { AnyAction, Dispatch } from 'redux';
import { batch } from 'react-redux';
import {
  clearDataModel,
  setDataModel,
  setDataModelFiles,
  setDataModelSharedFolders,
  setGlobalEntity,
  setGlobalEntityAssetId,
  setIsErrorLoadingModel,
  setIsLoadingDataModel,
  setServiceIdForDataModel,
} from './dataModelReducer';
import { blazeDataClient } from '../../blazeData/blazeDataClient';
import {
  generateDataModelId,
  generateDataModelName,
  generateGlobalEntityName,
  globalEntityDependencyName,
} from '../../blazeData/blazeDataUtils';
import { isPrivateCloud } from '../../utils/bzmUserConfigUtil';
import _ from 'lodash';
import { DataModel, DataModelFile, MeModel, isEffectiveModel } from '@blazemeter/blaze-data-model';
import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { Service } from '../../types/rest/assetCatalogTypes';
import i18n from '../../i18n';
import { ModelManipulator } from '@blazemeter/blaze-data-designer-client';
import download from 'downloadjs';
import { CSVFile, DataModelState } from './dataModelTypes';
import { getARBaseUrl } from '../../axios/tdm-http-client';
import { fetchFolder, requestLoadFolders } from '../sharedFolders/sharedFoldersActions';
import { SharedFolder } from '@blazemeter/blaze-data-ui';
import { ManipulatorDataModel } from '@blazemeter/blaze-data-designer-client/src/index';
import {
  DataDesignerClientAr,
  GlobalEntity,
} from '@blazemeter/blaze-data-designer-client/lib/client/DataDesignerClientAr';
import { AssetTypes } from '@blazemeter/asset-repository-lib/lib/type/asset-types';
import store from '../configureStore';
import { b64encode, getErrorMessage } from '../../utils/miscUtil';
import { Base64 } from 'js-base64';
import MixpanelService from '../../utils/mixpanelService';
import { DATA_MODEL_LOAD_EXCEPTION, DATA_MODEL_RECREATED, DATA_MODEL_WRONG_ASSETS } from '../../utils/mixpanelEvents';

const getDataModelByName = async (ddClient: DataDesignerClientAr, assetName: string) => {
  const modelAsset = await ddClient.getAssetsByName(assetName, undefined, AssetTypes.DATA_MODEL);
  const modelAssetResult = modelAsset.body.result;

  for (let i = 0; i < modelAssetResult.length; i++) {
    const dataModelResponse = await ddClient.getDataModel(modelAssetResult[i].id || '');
    if (dataModelResponse) {
      return dataModelResponse.body.result;
    }
  }
  return undefined;
};

/**
 * Gets Data Model by Service id from Asset Repository.
 * @param serviceId
 * @param raiseError
 */
export const getDataModelForService = async (
  serviceId: number,
  raiseError?: boolean,
  skipGlobalEntityFetch?: boolean,
): Promise<DataModel | undefined> => {
  let dataModel = undefined;
  // the client may not be initialized yet
  if (!_.isEmpty(blazeDataClient.client)) {
    try {
      const ddClient = blazeDataClient.client;
      //Get Service Placeholder asset
      const linksResponse = await ddClient.getAssetsByName(
        generateDataModelId(serviceId),
        undefined,
        AssetTypes.MOCK_SERVICE,
      );
      if (linksResponse && linksResponse.body && linksResponse.body.result) {
        const result = linksResponse.body.result;
        if (result[0] && result[0].id) {
          const linkId = result[0].id;
          //Get Service Dependencies
          const dmResponse = await ddClient.getAssetDependencies(linkId);
          if (dmResponse.body && dmResponse.body.result) {
            // Filter dependency by type mock svc type, the assetName must end with the service id
            const dMAssets: any = dmResponse.body.result.filter(
              asset => asset.type === AssetTypes.MOCK_SERVICE && asset.assetName.endsWith(`-${serviceId}`),
            );

            // Wrong asset linked to the service, which do not follow the naming convention,
            // this may cause issues with loading the data model, alert the user about it
            const wrongDMassets: any = dmResponse.body.result.filter(
              asset => asset.type === AssetTypes.MOCK_SERVICE && !asset.assetName.endsWith(`-${serviceId}`),
            );

            if (!_.isEmpty(wrongDMassets)) {
              MixpanelService.track(
                DATA_MODEL_WRONG_ASSETS(
                  serviceId,
                  wrongDMassets.map((a: any) => a.id),
                ),
              );
            }

            if (dMAssets && Array.isArray(dMAssets)) {
              // for broken assets, where there are multiple mock-svc dependencies
              // find the dependency where is the actual non-empty data model
              for (let i = 0; i < dMAssets.length; i++) {
                try {
                  const dm = await getDataModelByName(ddClient, dMAssets[i].assetName);
                  if (dm) {
                    dataModel = dm;
                    break;
                  }
                } catch (err) {
                  console.error(`Error during retrieving data model ${dMAssets[i].assetName}`, err);
                }
              }
            }

            //Global entity dependencies, store to redux if there is a global entity available
            const geAssets: any = dmResponse.body.result.filter(asset => asset.type === globalEntityDependencyName);
            if (!_.isEmpty(geAssets) && skipGlobalEntityFetch !== true) {
              await fetchGlobalEntityData(geAssets[0].assetName);
            } else if (_.isEmpty(geAssets)) {
              clearGlobalEntity();
            }
          }
        }
      }
    } catch (e) {
      if (raiseError) {
        throw e;
      }
    }
  }
  return dataModel;
};

/**
 * Gets Global Entity data from dependency object and stores them to redux.
 * @param geAssetName
 */
const fetchGlobalEntityData = async (geAssetName: any) => {
  try {
    if (!_.isNil(geAssetName)) {
      const ddClient = blazeDataClient.client;
      const geAssets: any = (await ddClient.getAssetsByName(geAssetName, undefined, AssetTypes.GLOBAL_ENTITY)).body
        .result;
      const assetId = geAssets[0].id;
      const data: any = await getAssetData(geAssets[0].id);
      await store.dispatch(setGlobalEntity(data));
      await store.dispatch(setGlobalEntityAssetId(assetId));
    }
  } catch (e) {
    store.dispatch(showToastNotification(ToastType.Danger, 'Cannot fetch Global Entity.', getErrorMessage(e)));
  }
};

/**
 * Loads data model for service, if data model does not exist yet it is going to be created.
 * Link to Model is created when data panel is open or explicit requested by createLink param.
 * @param serviceId
 * @param forceReload
 * @param createLink
 */
export const loadDataModelForService = (serviceId: number, forceReload?: boolean, createLink?: boolean) => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    if (!isPrivateCloud()) {
      if (serviceId === 0) {
        dispatch(clearDataModel());
        return;
      }
      // if the filter changed load the links
      if (forceReload || getState().dataModel.serviceId !== serviceId) {
        batch(() => {
          dispatch(clearDataModel());
          dispatch(setIsLoadingDataModel(true));
          dispatch(setServiceIdForDataModel(serviceId));
        });
        let dataModel;
        try {
          dataModel = await getDataModelForService(serviceId, true);
          if (dataModel) {
            const manipulatorModel = await blazeDataClient.manipulator.load(dataModel);
            const paramCount = ModelManipulator.countProperties(manipulatorModel);
            dispatch(
              setDataModel({
                dataModel,
                serviceId,
                isLinked: true,
                paramCount,
              }),
            );
            await dispatch(loadDataModelCsvFiles());
            await dispatch(loadSharedFoldersForModel());
          } else {
            await initEmptyModel(serviceId, dispatch, getState, createLink);
          }
        } catch (e) {
          MixpanelService.track(
            DATA_MODEL_LOAD_EXCEPTION(
              serviceId,
              !!forceReload,
              !!createLink,
              _.get(e, 'message', e?.toString?.() || ''),
            ),
          );

          if (
            e &&
            e
              .toString()
              .toLowerCase()
              .includes('not found')
          ) {
            // the extended entity was removed from workspace
            console.log('Asset was removed, recreating the model.', e);
            MixpanelService.track(DATA_MODEL_RECREATED(serviceId, dataModel?.id, dataModel?.title));
            if (dataModel) {
              const emptyModel = blazeDataClient.manipulator.createEmptyModel('sdm', dataModel.title, '');
              const m = await blazeDataClient.manipulator.extractDataModel(emptyModel);
              m.id = dataModel.id;
              dispatch(
                setDataModel({
                  dataModel: m,
                  serviceId,
                  isLinked: true,
                  isLoading: false,
                  paramCount: 0,
                }),
              );
            }
          } else {
            dispatch(
              showToastNotification(
                ToastType.Danger,
                i18n.t('common:tdm.dmLoadFailed'),
                _.get(e, 'response.data.error', ''),
              ),
            );
            dispatch(setIsErrorLoadingModel(true));
            dispatch(setIsLoadingDataModel(false));
          }
        }
      }
    }
  };
};

export const addEntityToModel = async (
  manipulatorDataModel: ManipulatorDataModel,
  entityName: string,
): Promise<ManipulatorDataModel> => {
  const manipulator = blazeDataClient.manipulator;
  const result = await manipulator.addEntity(manipulatorDataModel, entityName, '');
  return result.model;
};

export const addParameterToEntity = async (
  manipulatorDataModel: ManipulatorDataModel,
  entityName: string,
  parameter: string,
): Promise<ManipulatorDataModel> => {
  const manipulator = blazeDataClient.manipulator;

  const list = _.map(manipulatorDataModel.entities[entityName].propertiesGroups, (key, value) => value);
  const propertiesGroupName = list.length > 0 ? list[0] : 'default';
  await manipulator.addProperty(manipulatorDataModel, parameter, propertiesGroupName, entityName);
  return manipulator.setPropertyValue(manipulatorDataModel, parameter, '""', entityName);
};

const initEmptyModel = async (
  serviceId: number,
  dispatch: ThunkDispatch<DataModelState, any, AnyAction>,
  getState: any,
  createLink?: boolean,
) => {
  const services: Service[] = getState().services.services;
  const service: Service | undefined = services?.find((s: Service) => s.id === serviceId);
  if (!service) {
    throw new Error(`Cannot initialize data model: service ${serviceId} is unavailable`);
  }

  try {
    const manipulator = blazeDataClient.manipulator;
    const modelName = generateDataModelName(service);
    const emptyModel = manipulator.createEmptyModel('sdm', modelName, '');
    const m = await manipulator.extractDataModel(emptyModel);
    const isPanelOpened = getState().ui.isDDPanelOpened;
    await dispatch(
      setDataModel({
        dataModel: m,
        serviceId,
        isLinked: false,
        isLoading: isPanelOpened,
        paramCount: 0,
      }),
    );
    if (isPanelOpened || createLink === true) {
      await dispatch(createLinkToModel(serviceId));
    }

    //Reset Global asset
    clearGlobalEntity();
  } catch (e) {
    dispatch(
      showToastNotification(ToastType.Danger, i18n.t('common:tdm.dmLoadFailed'), _.get(e, 'response.data.error', '')),
    );
    dispatch(setServiceIdForDataModel(serviceId));
    dispatch(setIsErrorLoadingModel(true));
  }
};

export const clearGlobalEntity = () => {
  store.dispatch(setGlobalEntity(undefined));
  store.dispatch(setGlobalEntityAssetId(undefined));
};

/**
 * Creates Service Asset object and dependency.
 * @param serviceId
 */
export const createLinkToModel = (serviceId: number): ThunkAction<any, DataModelState, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any) => {
    //If model is not linked yet
    if (!getState().dataModel.isLinked) {
      dispatch(setIsLoadingDataModel(true));
      try {
        const model = getState().dataModel.dataModel;
        const ddClient = blazeDataClient.client;
        const msDataModelId = generateDataModelId(serviceId);

        // try to find the Service Asset
        const assets = await ddClient.getAssetsByName(msDataModelId, undefined, AssetTypes.MOCK_SERVICE);
        let svcAssetId: string;
        if (_.isEmpty(assets.body.result)) {
          // create Service placeholder
          const svcPlc = await ddClient.saveMockSvcModelAsAsset(msDataModelId);
          svcAssetId = svcPlc.mockSvcAssetResponse.body.result?.id || '';
        } else {
          svcAssetId = assets.body.result[0].id || '';
        }
        // create Data Model in Asset Repository
        await createDataModelInAssetRepository(model, svcAssetId, dispatch, serviceId);
      } catch (e) {
        dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.dmSaveFailed'), getErrorMsg(e)));
      }
    }
  };
};

export const updateAssetData = async (assetId: string, globalEntity: GlobalEntity) => {
  if (_.isNil(assetId)) {
    throw Error('Missing Asset ID');
  }
  try {
    const arClient = blazeDataClient.arClient;
    arClient.updateAssetData(assetId, globalEntity);
  } catch (e) {
    store.dispatch(showToastNotification(ToastType.Danger, 'Cannot save Global Entity', getErrorMessage(e)));
  }
};

export const getAssetData = async (assetId: string) => {
  if (_.isNil(assetId)) {
    throw Error('Missing Asset ID');
  }
  const arClient = blazeDataClient.arClient;
  const assetBody = (await arClient.getAssetData(assetId)).body;
  return assetBody;
};

/**
 * Gets global entity for service.
 * @param serviceId
 */
export const getGlobalEntityForService = async (serviceId: number) => {
  const geAssetName = generateGlobalEntityName(serviceId);
  return await getGlobalEntityAssetByGlobalEntityName(geAssetName);
};

/**
 * Gets global entity from asset repository.
 * @param globalEntityName
 */
export const getGlobalEntityAssetByGlobalEntityName = async (globalEntityName: string) => {
  const ddClient = blazeDataClient.client;
  try {
    const globalEntityAsset = await ddClient.getAssetsByName(globalEntityName, undefined, AssetTypes.GLOBAL_ENTITY);
    const error = _.get(globalEntityAsset, 'body.error');
    if (!_.isNil(error)) {
      throw Error(`Cannot get Global Entity. Reason: ${error}`);
    }
    return globalEntityAsset.body.result[0];
  } catch (e) {
    store.dispatch(showToastNotification(ToastType.Danger, getErrorMessage(e)));
  }
  return null;
};

/**
 * Creates and saves global entity asset.
 * @param fullMockSvcId virtual service identifier in asset repo
 * @param globalEntity object to be saved
 *
 * @return saved global entity asset
 */
export const saveGlobalEntityToAssetRepository = async (serviceId: number, globalEntity: GlobalEntity) => {
  const ddClient = blazeDataClient.client;
  try {
    const rsp = await ddClient.saveGlobalEntityAsAsset(globalEntity, generateGlobalEntityName(serviceId));
    const globalEntityAsset = rsp.globalEntityAssetResponse.body.result;

    //Get ID of the service asset
    const svcAssetRsp: any = await ddClient.getAssetsByName(
      generateDataModelId(serviceId),
      undefined,
      AssetTypes.MOCK_SERVICE,
    );
    const svcAssetId = svcAssetRsp.body.result[0].id;

    if (!_.isNil(globalEntityAsset.id) && !_.isNil(svcAssetId)) {
      //Link the Global asset with the Service Asset
      await ddClient.createAssetDependencyBetweenAssets(svcAssetId, globalEntityAsset.id, globalEntityDependencyName);
    } else {
      store.dispatch(showToastNotification(ToastType.Warning, 'Asset Dependency link was not created.'));
    }
    return globalEntityAsset;
  } catch (e) {
    store.dispatch(
      showToastNotification(
        ToastType.Danger,
        'Unable to save Global Entity to Blaze Data backend.',
        getErrorMessage(e),
      ),
    );
  }

  return null;
};

const createDataModelInAssetRepository = async (model, svcAssetId, dispatch, serviceId) => {
  const ddClient = blazeDataClient.client;

  try {
    const dmAsset = await ddClient.saveDataModelAsAsset(_.cloneDeep(model), false, true);
    const dmAssetId = dmAsset.modelAssetResponse.body.result?.id;
    if (svcAssetId && dmAssetId) {
      // create dependencies
      await ddClient.createAssetDependencyBetweenAssets(svcAssetId, dmAssetId, AssetTypes.MOCK_SERVICE);
      const m = _.cloneDeep(model);
      m.id = dmAssetId;
      await dispatch(
        setDataModel({
          dataModel: m,
          serviceId,
          isLinked: true,
          isLoading: false,
          paramCount: 0,
        }),
      );
    }
  } catch (e) {
    console.error(e);
  }
};

const updateRequestCountersByService = new Map<number, number>();

export const updateDataModel = (dataModel: DataModel) => {
  return async (dispatch: Dispatch, getState: any): Promise<void> => {
    const serviceIdAtCallTime = getState().dataModel.serviceId;
    const requestId = (updateRequestCountersByService.get(serviceIdAtCallTime) || 0) + 1;
    updateRequestCountersByService.set(serviceIdAtCallTime, requestId);
    try {
      dataModel.id = getState().dataModel.dataModel.id; // make sure to use the correct UUID
      dataModel.title = getState().dataModel.dataModel.title; // and also title as otherwise the asset would not be findable
      const response = await callUpdateDataModel(dataModel);
      if (
        requestId !== updateRequestCountersByService.get(serviceIdAtCallTime) ||
        getState().dataModel.serviceId !== serviceIdAtCallTime
      ) {
        return;
      }
      const m = response.body.result;
      if (!m || !isEffectiveModel(m)) {
        dispatch(
          showToastNotification(ToastType.Danger, i18n.t('common:tdm.dmUpdateFailed'), 'Invalid response from server'),
        );
        return;
      }
      const manipulatorModel = await blazeDataClient.manipulator.load(m);
      const paramCount = ModelManipulator.countProperties(manipulatorModel);
      dispatch(
        setDataModel({
          dataModel: m,
          serviceId: serviceIdAtCallTime,
          isLinked: true,
          paramCount,
          files: getState().dataModel.files,
          sharedFolders: getState().dataModel.sharedFolders,
        }),
      );
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.dmUpdateFailed'), getErrorMsg(e)));
    }
  };
};

export const callUpdateDataModel = (dataModel: DataModel) => {
  return blazeDataClient.client.updateDataModel(dataModel);
};

const uploadCSVFile = async (file: File, getState: any, dispatch: Dispatch) => {
  const data = b64encode(await file.text());
  const modelId = getState().dataModel.dataModel.id;
  const fl = {
    name: file.name,
    modelId,
    data,
  } as DataModelFile;
  const files = await getDataModelFiles(getState);
  const existingFile = files ? files.find((f: CSVFile) => f.name === file.name) : null;
  if (existingFile) {
    fl.id = existingFile.id;
    try {
      await blazeDataClient.client.dataModelFilesUpdate(fl);
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileUpdateFailed'), getErrorMsg(e)));
    }
  } else {
    try {
      await blazeDataClient.client.dataModelFilesCreate(fl);
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileCreateFailed'), getErrorMsg(e)));
    }
  }
};

/**
 * Blaze Data Panel returns normalized CSV file names but in backend they are in the non-normalized version
 */
const getNormalizedCsvName = (name: string) => {
  // support alpha numeric, underscore, dot and hyphen
  return name.replace(/[^a-zA-Z0-9 _.-]/g, '_');
};

const getDataModelFiles = async (getState: any) => {
  const modelId = getState().dataModel.dataModel.id;
  const filesResponse = await blazeDataClient.client.dataModelFilesGetAll(modelId);
  return filesResponse.body.result;
};

const getDataModelFile = async (fileName: string, getState: any) => {
  const result = await getDataModelFiles(getState);
  if (!_.isEmpty(result)) {
    const normalizedName = getNormalizedCsvName(fileName);
    return result.find(f => getNormalizedCsvName(f.name) === normalizedName);
  }
};

export const loadDataModelCsvFiles = (sharedFoldersIds?: string[]) => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    if (getState().dataModel.isLinked) {
      const result = await getDataModelFiles(getState);
      const arBaseUrl = getARBaseUrl() || '';
      const workspaceId = getState().blazeUserConfig.blazeUserConfig.user.activeWorkspaceId;
      const resultFiles: CSVFile[] = [];
      const getLink = (id: string) =>
        new URL(`workspaces/${workspaceId}/assets/${id}/data`, `${arBaseUrl}api/v1/`).href;
      if (!_.isEmpty(result)) {
        resultFiles.push(
          ...result.map(f => ({
            id: f.id,
            name: getNormalizedCsvName(f.name),
            link: getLink(f.id || ''),
          })),
        );
      }
      const sharedFiles = sharedFoldersIds
        ? await loadSharedFolderCsvFilesByFolderIds(getState, sharedFoldersIds)
        : await loadSharedFolderCsvFiles(getState);
      if (sharedFiles) {
        resultFiles.push(...sharedFiles);
      }
      dispatch(setDataModelFiles(resultFiles));
    }
  };
};

export const deleteCSVFile = (fileName: string) => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    try {
      const file = await getDataModelFile(fileName, getState);
      if (file && file.id) {
        await blazeDataClient.client.dataModelFilesDelete(file.id);
      }
    } catch (e) {
      console.error(e);
      //dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileDeleteFailed'), getErrorMsg(e)));
    }
  };
};

export const downloadCSVFile = (fileName: string) => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    try {
      if (isSharedFolderFile(fileName)) {
        const csvFile: CSVFile = getState().dataModel.files.find((f: CSVFile) => f.name === fileName);
        if (csvFile && csvFile.link) {
          download(csvFile.link);
        } else {
          dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileDownloadFailed')));
        }
      } else {
        const file = await getDataModelFile(fileName, getState);
        if (file && file.id) {
          const fileResult = await blazeDataClient.client.dataModelFilesGetById(file.id);
          // base64 is missing charactes which takes more than 1 byte (contains only ASCI characters)
          // Uint8Array contains UTF-8 characters
          // https://stackoverflow.com/a/30106551
          const str = Base64.decode(fileResult.body.result.data);
          const enc = new TextEncoder();
          const data = enc.encode(str);
          if (data) {
            download(data, file.name, 'text/csv');
          }
        } else {
          dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileDownloadFailed')));
        }
      }
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.csvFileDownloadFailed'), getErrorMsg(e)));
    }
  };
};

export const uploadCSVFiles = (files: File[]) => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const promises = files.map(f => uploadCSVFile(f, getState, dispatch));
        Promise.all(promises).then(() => {
          dispatch(loadDataModelCsvFiles());
          resolve();
        });
      } catch (e) {
        reject(e);
      }
    });
  };
};

export const getSharedFilesFromModel = (getState: any, model?: MeModel) => {
  const dataModel = model ? model : getState().dataModel.dataModel;
  if (dataModel && dataModel.entities) {
    const files: string[] = [];
    for (const entityName in dataModel.entities) {
      if (dataModel.entities.hasOwnProperty(entityName) && dataModel.entities[entityName].datasources) {
        try {
          const entityFiles = Object.values(dataModel.entities[entityName].datasources)
            .filter((ds: any) => ds.type === 'csv' && isSharedFolderFile(ds.name))
            .map((ds: any) => {
              return ds.name as string;
            });
          files.push(...entityFiles);
        } catch (e) {
          console.log(e);
        }
      }
    }
    return files;
  }
  return [];
};

export const getSharedFoldersFromFiles = async (files: string[], getState: any) => {
  if (!_.isEmpty(files)) {
    // get unique folder names from the file names
    const folderNames = _.uniq(files.map(f => f.substring(0, f.indexOf('/'))));
    const workspaceId = getState().blazeUserConfig.blazeUserConfig.user.activeWorkspaceId;
    const sharedFoldersResponse = await requestLoadFolders(1000, 0, workspaceId, getState);
    const sharedFolders: SharedFolder[] = sharedFoldersResponse.data.result;
    const modelFolders: string[] = [];
    folderNames.forEach(f => {
      const sf = sharedFolders.find(sf => sf.name === f);
      if (sf && sf.id) {
        modelFolders.push(sf.id);
      }
    });
    return modelFolders;
  }
};

const loadSharedFolderCsvFilesByFolderIds = async (getState: any, sharedFoldersIds: string[]) => {
  const promises = sharedFoldersIds.map(fId => fetchFolder(fId, getState));
  const resFiles: CSVFile[] = [];
  return Promise.all(promises).then(result => {
    result.forEach(sf => {
      const csvFiles = sf.files.filter(f => f.name.toLowerCase().endsWith('.csv'));
      const resWFixedNames = csvFiles.map(sff => Object.assign({}, sff, { name: sf.folderName + '/' + sff.name }));
      resFiles.push(...resWFixedNames);
    });
    return resFiles;
  });
};

export const loadSharedFolderCsvFiles = async (getState: any, dataModel?: MeModel) => {
  const files = getSharedFilesFromModel(getState, dataModel);
  const folders = await getSharedFoldersFromFiles(files, getState);
  if (folders) {
    const promises = folders.map(fId => fetchFolder(fId, getState));
    const resFiles: CSVFile[] = [];
    return Promise.all(promises).then(result => {
      result.forEach(sf => {
        const filesInFolder = files.filter(f => f.substring(0, f.indexOf('/')) === sf.folderName);
        const fileNames = filesInFolder.map(f => f.substring(f.indexOf('/') + 1));
        const res = sf.files.filter(f => fileNames.includes(f.name));
        // prepend the name of the shared folder
        const resWFixedNames = res.map(sff => Object.assign({}, sff, { name: sf.folderName + '/' + sff.name }));
        resFiles.push(...resWFixedNames);
      });
      return resFiles;
    });
  }
};

export const loadSharedFoldersForModel = () => {
  return async (dispatch: ThunkDispatch<DataModelState, any, AnyAction>, getState: any): Promise<void> => {
    const files = getSharedFilesFromModel(getState);
    const modelFolders = (await getSharedFoldersFromFiles(files, getState)) || [];
    dispatch(setDataModelSharedFolders(modelFolders));
  };
};

const getErrorMsg = (e: any) => _.get(e, 'response.data.error', e);

const isSharedFolderFile = (name: string) => name.indexOf('/') > -1;
